import { Fragment, type Ref } from 'react';
import { ChevronDown, ChevronRight, Folder, Pencil } from 'lucide-react';
import { CatalogItemRow, type CatalogItem, type Photo, type Schematic } from './CatalogItemRow';
import type { Job3D } from '../threeDApi';

type NameCol = { label: string; get: (it: CatalogItem) => string };

// Group items by category for the folder view: every known category A->Z (shown even
// when empty, so a just-added category appears right away), then the blank/uncategorized
// bucket last under a translated label. Returned shape drives CatalogCategoryGroup.
export function groupByCategory(
  items: CatalogItem[],
  allCategories: string[],
  uncategorizedLabel: string,
) {
  const map = new Map<string, CatalogItem[]>();
  for (const c of allCategories) {
    const key = c.trim();
    if (key) map.set(key, []);
  }
  for (const it of items) {
    const key = it.category?.trim() || '';
    (map.get(key) ?? map.set(key, []).get(key)!).push(it);
  }
  // The caller's order is kept, not re-sorted: it arrives with each super-category
  // followed by the categories inside it, which alphabetical order would shuffle apart.
  const groups = [...map.entries()]
    .filter(([k]) => k !== '')
    // label = shown name; value = the real category string ('' for uncategorized),
    // used as the drop target when reassigning by drag & drop.
    .map(([value, groupItems]) => ({ label: value, value, items: groupItems }));
  const blank = map.get('');
  if (blank && blank.length) groups.push({ label: uncategorizedLabel, value: '', items: blank });
  return groups;
}

// One category folder in the Parts DB table: its header row (folder, chevron, count,
// and the pencil that edits the category) plus a CatalogItemRow for each of its items.
// Extracted from NoticeCatalog to keep that file under the size limit; it owns the
// state, this only draws one group and reports the gestures back up.
export function CatalogCategoryGroup({
  group,
  modelsByItem,
  collapsed,
  colCount,
  label,
  canEditCategory,
  depth = 0,
  isParent = false,
  childCount = 0,
  dragOver,
  draggingId,
  nameCols,
  cellCls,
  linkedId,
  linkedRef,
  onToggle,
  onEditCategory,
  onCatDragOver,
  onDropHere,
  onDropOnRow,
  draggingCat,
  onCatDragStart,
  onCatDragEnd,
  onDropCategory,
  onRowDragStart,
  onRowDragEnd,
  onEditItem,
  onZoomSchematic,
  onZoomPhoto,
  onOpenModel,
  t,
}: {
  group: { label: string; value: string; items: CatalogItem[] };
  // The Parts DB's meshes, by catalog item id. Passed whole rather than per item so one
  // fetch serves every row.
  modelsByItem: Map<number, Job3D[]>;
  collapsed: boolean;
  colCount: number;
  // The category's shown name (localized), already resolved by the caller.
  label: string;
  canEditCategory: boolean;
  // 1 for a category inside a super-category, so the folder sits under the one above it.
  depth?: number;
  // A super-category: it holds categories, not parts. Nothing can be dropped on it.
  isParent?: boolean;
  // How many categories are inside it — what a super-category counts instead of items.
  childCount?: number;
  dragOver: boolean;
  // The id of the row being dragged, or null — a row highlights when it is that one.
  draggingId: number | null;
  nameCols: NameCol[];
  cellCls: string;
  linkedId: number | null;
  linkedRef: Ref<HTMLTableRowElement>;
  onToggle: () => void;
  onEditCategory: () => void;
  onCatDragOver: () => void;
  onDropHere: () => void;
  // Dropped ON a row rather than on the folder. Same gesture, two meanings: from
  // another folder it files the part here, and from this one it puts it in front of
  // the row it was dropped on.
  onDropOnRow: (id: number) => void;
  // The folder being dragged, or null. Reordering folders is the same gesture as filing a
  // part, one level up, so the two share the header and are told apart by which is in
  // flight rather than by where you aim.
  draggingCat: string | null;
  onCatDragStart: () => void;
  onCatDragEnd: () => void;
  onDropCategory: () => void;
  onRowDragStart: (id: number) => void;
  onRowDragEnd: () => void;
  onEditItem: (item: CatalogItem) => void;
  onZoomSchematic: (s: Schematic) => void;
  onZoomPhoto: (p: Photo) => void;
  onOpenModel: (job: Job3D) => void;
  t: (k: string) => string;
}) {
  const dragging = draggingId != null;
  // The header answers TWO drags now, and which one is in flight decides what a drop on
  // it means: a part being filed into this folder, or a folder being put in front of it.
  // They never overlap — one of the two is null — so the header does not have to ask what
  // is being dragged, only which question it is being asked.
  const movingCategory = draggingCat != null && draggingCat !== group.value;
  return (
    <Fragment>
      {/* Category (folder) header — a drop target for rows being filed here AND for other
          folders being reordered, and itself draggable. */}
      <tr
        onClick={onToggle}
        // Draggable only when it is a real category. The uncategorized bucket is not one:
        // it has no row in the ontology, so there is nothing to give a position to.
        draggable={canEditCategory}
        onDragStart={(e) => {
          e.stopPropagation();
          // Firefox starts no drag without a payload, exactly as the item rows found.
          e.dataTransfer.setData('text/plain', group.value);
          e.dataTransfer.effectAllowed = 'move';
          onCatDragStart();
        }}
        onDragEnd={onCatDragEnd}
        onDragOver={(e) => {
          // preventDefault is what tells the browser a drop is allowed here, so NOT
          // calling it is the refusal. A super-category refuses a part rather than filing
          // it one level too high; it accepts a folder, because reordering is exactly
          // what a super-category can take part in.
          if (movingCategory || (dragging && !isParent)) {
            e.preventDefault();
            onCatDragOver();
          }
        }}
        onDrop={(e) => {
          if (movingCategory) {
            e.preventDefault();
            e.stopPropagation();
            onDropCategory();
            return;
          }
          if (isParent) return;
          e.preventDefault();
          onDropHere();
        }}
      >
        <td
          colSpan={colCount}
          style={depth ? { paddingLeft: `${1 + depth * 1.5}rem` } : undefined}
          className={`cursor-pointer select-none border border-slate-300 px-4 py-2 text-left text-sm font-semibold text-slate-700 transition-colors ${
            dragOver ? 'bg-blue-100' : 'bg-slate-200'
          }`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              {/* The chevron is the whole hint that the row does anything: a folder icon
                  says what this is, not that it opens. */}
              {collapsed ? (
                <ChevronRight className="h-4 w-4 text-slate-400" />
              ) : (
                <ChevronDown className="h-4 w-4 text-slate-400" />
              )}
              {/* Green marks a folder INSIDE another one, grey a top-level folder. By
                  depth and not by whether it takes parts: Tools sits at the top and takes
                  parts, and colouring it as the odd one out drew the eye to the least
                  interesting thing on the row. The refusal to accept a drop is still
                  about super-categories, and is shown by the drop not highlighting. */}
              <Folder className={`h-4 w-4 ${depth
                ? 'text-blue-600'
                : 'text-slate-400'}`} />
              {label}
              {/* A super-category counts the categories inside it; everything else counts
                  its parts. The parts under Hardware are Screws', not Hardware's, and
                  summing them here would state a number no row of this table holds. */}
              <span className="text-xs font-normal text-slate-400">
                ({isParent ? childCount : group.items.length})
              </span>
            </span>
            {/* One pencil: edit and delete both live in what it opens. Not the
                Uncategorized bucket — that is the absence of a category, not one. */}
            {canEditCategory && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditCategory();
                }}
                title={t('notice.catalog.editCategory')}
                aria-label={t('notice.catalog.editCategory')}
                className="rounded p-1 text-slate-400 transition-colors hover:text-blue-500"
              >
                <Pencil className="h-4 w-4" />
              </button>
            )}
          </div>
        </td>
      </tr>
      {!collapsed &&
        group.items.map((it) => (
          <CatalogItemRow
            key={it.id}
            item={it}
            models={modelsByItem.get(it.id) ?? []}
            rowRef={it.id === linkedId ? linkedRef : undefined}
            linked={it.id === linkedId}
            dragging={draggingId === it.id}
            nameCols={nameCols}
            cellCls={cellCls}
            onDragStart={() => onRowDragStart(it.id)}
            onDragEnd={onRowDragEnd}
            onDragOver={() => dragging && onCatDragOver()}
            onDrop={() => onDropOnRow(it.id)}
            onEdit={() => onEditItem(it)}
            onZoomSchematic={onZoomSchematic}
            onZoomPhoto={onZoomPhoto}
            onOpenModel={onOpenModel}
            t={t}
          />
        ))}
    </Fragment>
  );
}
